import { pxlsToPts } from '../pageLayoutMath';
import { getNewContentPosition } from '../../Utils/SlateUtils';

/**
 * Determine which normal-path scenario applies for a footnote-wrapper and
 * return a pure operation descriptor.  The caller applies the descriptor to
 * the mutable accumulators (fnwToBeMoved, boundaries, actualHeight, etc.).
 *
 * Only called when `!hasAnomalies && !listNeedModifications`.
 *
 * @param {{
 *   fnwNode:                    object,   - editor.children[index] (Slate node)
 *   index:                      number,
 *   editorChildren:             Array,    - editor.children (for parentNode lookup)
 *   isLastDocNode:              boolean,
 *   currentHeight:              number,   - px
 *   footNoteWrapperHeight:      number,   - px
 *   elBeforeHeight:             number,   - px
 *   elBeforeMarginBottom:       number,   - px
 *   elBeforePrevIndexMarginBottom: number, - px (element at index - 2)
 *   availableHeightAfterFNW:    number,   - px
 *   availableSpaceForFNW:       number,   - px
 *   pageHeightInPxs:            number,   - px
 *   heightToUse:                number,   - px (single-page usable height for this page)
 *   defaultMarginTopInPxs:      number,   - px (ptsToPxls(10))
 *   nextElementHeight:          number,   - px
 *   nextElementBottomMargin:    number,   - px
 *   fnWLists:                   Array,    - pre-built from collectFootnoteData
 *   superscriptsObject:         object,   - keyed by superscript text
 *   fnwToBeMoved:               Array,    - read-only snapshot (for findIndex)
 *   newFootnoteWrappersCreated: Array,    - read-only snapshot
 *   hasRelocatedFootnoteContent: object,  - read-only snapshot
 *   hasSuperscriptAfter:        Array|null, - checkIfItHasSuperscript(editor, index+1)
 *   hasSuperscriptBefore:       Array|null, - checkIfItHasSuperscript(editor, index-1)
 *   tempContentToDelete:        Array,
 *   currentBoundaryIndex:       number,
 *   boundaries:                 Array,
 * }}
 * @returns {{
 *   scenario:              number,          - 1 | 2 | 3 | 4
 *   newActualHeight:       number,
 *   fnwToMove:             Array,           - push these to fnwToBeMoved
 *   fnwToUpdate:           {index,item}|null, - update fnwToBeMoved[index]
 *   fnwToDelete:           Array,           - push these to deletedFootnoteWrappers
 *   newFnwToAdd:           Array,           - push these to newFootnoteWrappersCreated
 *   newFnwToUpdate:        {index,item}|null, - update newFootnoteWrappersCreated[index]
 *   relocatedContent:      {key,value}|null, - set hasRelocatedFootnoteContent[key]
 *   newBoundaryAt:         Array|null,      - if set: boundaries[currentBoundaryIndex].at
 *   setUpdatedBoundaries:  boolean,
 * }}
 */
export function selectFootnoteScenario({
	fnwNode,
	index,
	editorChildren,
	isLastDocNode,
	currentHeight,
	footNoteWrapperHeight,
	elBeforeHeight,
	elBeforeMarginBottom,
	elBeforePrevIndexMarginBottom,
	availableHeightAfterFNW,
	availableSpaceForFNW,
	pageHeightInPxs,
	heightToUse,
	defaultMarginTopInPxs,
	nextElementHeight,
	nextElementBottomMargin,
	fnWLists,
	superscriptsObject,
	fnwToBeMoved,
	newFootnoteWrappersCreated,
	hasRelocatedFootnoteContent,
	hasSuperscriptAfter,
	hasSuperscriptBefore,
	tempContentToDelete,
	currentBoundaryIndex,
	boundaries,
}) {
	const pageHeightInPts = pxlsToPts(pageHeightInPxs);

	// Initialise descriptor — caller applies each field to its accumulator.
	const desc = {
		scenario: null,
		newActualHeight: currentHeight,
		fnwToMove: [],
		fnwToUpdate: null,
		fnwToDelete: [],
		newFnwToAdd: [],
		newFnwToUpdate: null,
		relocatedContent: null,
		newBoundaryAt: null,
		setUpdatedBoundaries: false,
	};

	// ── SCENARIO SELECTION ─────────────────────────────────────────────────

	if (availableHeightAfterFNW > defaultMarginTopInPxs) {
		// Enough vertical space — check if the next element also fits.
		const marginToSubtract = nextElementBottomMargin > defaultMarginTopInPxs
			? nextElementBottomMargin
			: defaultMarginTopInPxs;
		const extraFootnoteContent = hasSuperscriptAfter
			? superscriptsObject[hasSuperscriptAfter[0].text]
			: null;
		const availableSpaceAfterNextElement = nextElementHeight > 0
			? availableSpaceForFNW
				- elBeforeMarginBottom
				- footNoteWrapperHeight
				- nextElementHeight
				- marginToSubtract
				- (extraFootnoteContent ? extraFootnoteContent.contentHeight : 0)
			: null;

		if (!isLastDocNode && availableSpaceAfterNextElement != null && availableSpaceAfterNextElement >= 0) {
			// ── SCENARIO 1: move FNW down to index + 1, pull next element up ──

			desc.scenario = 1;

			const newMarginTopInPts = +(
				pageHeightInPts
				- pxlsToPts(currentHeight)
				- pxlsToPts(footNoteWrapperHeight)
				- pxlsToPts(nextElementHeight)
				- (extraFootnoteContent ? pxlsToPts(extraFootnoteContent.contentHeight) : 0)
			).toFixed(2);
			const newMarginTopInPxl = pageHeightInPxs
				- currentHeight
				- footNoteWrapperHeight
				- nextElementHeight
				- (extraFootnoteContent ? extraFootnoteContent.contentHeight : 0);

			const updatedFootnote = { ...fnwNode, marginTop: newMarginTopInPts };
			desc.fnwToMove.push({
				at: [index],
				to: [index + 1],
				element: updatedFootnote,
				contentToAdd: hasSuperscriptAfter
					? [{ ...extraFootnoteContent, newContentPath: [index + 1, updatedFootnote.length] }]
					: [],
				contentToDelete: tempContentToDelete,
			});

			if (hasSuperscriptAfter && extraFootnoteContent) {
				const parentIndex = extraFootnoteContent.contentPath && extraFootnoteContent.contentPath[0];
				const parentNode = parentIndex != null ? editorChildren[parentIndex] : null;

				if (parentNode && parentNode.children.length === 1) {
					desc.fnwToDelete.push({ at: parentIndex, delete: true });
				} else if (parentNode) {
					const indexOfFNWToupdate = fnwToBeMoved.length > 0
						? fnwToBeMoved.findIndex(
							(fnw) => fnw.at[0] === parentIndex && fnw.to[0] === parentIndex
						)
						: -1;

					if (indexOfFNWToupdate > -1) {
						const foundFNWToBeMoved = fnwToBeMoved[indexOfFNWToupdate];
						const updatedChildren = foundFNWToBeMoved.element.children.filter(
							(_child, idx) => idx !== extraFootnoteContent.contentPath[1]
						);
						const updatedElement = { ...foundFNWToBeMoved.element, children: updatedChildren };
						const updatedContentToDelete = foundFNWToBeMoved.contentToDelete
							? [...foundFNWToBeMoved.contentToDelete, extraFootnoteContent.contentPath[1]]
							: [extraFootnoteContent.contentPath[1]];
						desc.fnwToUpdate = {
							index: indexOfFNWToupdate,
							item: { ...foundFNWToBeMoved, element: updatedElement, contentToDelete: updatedContentToDelete },
						};
					} else {
						desc.fnwToMove.push({
							at: [parentIndex],
							to: [parentIndex],
							element: {
								...parentNode,
								children: parentNode.children.filter(
									(_child, idx) => idx !== extraFootnoteContent.contentPath[1]
								),
							},
							contentToDelete: tempContentToDelete.length > 0
								? [...tempContentToDelete, extraFootnoteContent.contentPath[1]]
								: [extraFootnoteContent.contentPath[1]],
						});
					}
				}
			}

			desc.newActualHeight = currentHeight
				+ footNoteWrapperHeight
				+ (extraFootnoteContent ? extraFootnoteContent.contentHeight : 0)
				+ newMarginTopInPxl
				- nextElementBottomMargin;
			desc.newBoundaryAt = [index + 1];
			desc.setUpdatedBoundaries = true;

		} else {
			// ── SCENARIO 2: keep FNW at index, adjust marginTop ──

			if (isLastDocNode && boundaries.length > 0) {
				// SCENARIO 2.1 — last document node
				desc.scenario = 2;
				const newMaxHeight = (currentHeight + footNoteWrapperHeight - elBeforeMarginBottom + defaultMarginTopInPxs) > pageHeightInPxs
					? pageHeightInPxs + heightToUse
					: pageHeightInPxs;
				const marginTopInPx = newMaxHeight - currentHeight - footNoteWrapperHeight + elBeforeMarginBottom;
				const newMarginTopInPts = +pxlsToPts(marginTopInPx).toFixed(2);
				const alreadyRight = newMarginTopInPts === fnwNode.marginTop;

				if (!alreadyRight) {
					desc.fnwToMove.push({
						at: [index],
						to: [index],
						element: { ...fnwNode, marginTop: newMarginTopInPts },
						contentToDelete: tempContentToDelete,
					});
				}
				desc.newActualHeight = pageHeightInPxs;

			} else {
				// SCENARIO 2.2 — normal case
				desc.scenario = 2;
				const newMarginTopInPxl = pageHeightInPxs - currentHeight - footNoteWrapperHeight + elBeforeMarginBottom;
				const newMarginTopInPts = +pxlsToPts(newMarginTopInPxl).toFixed(2);
				const alreadyRight = newMarginTopInPts === fnwNode.marginTop;

				if (!alreadyRight) {
					desc.fnwToMove.push({
						at: [index],
						to: [index],
						element: { ...fnwNode, marginTop: newMarginTopInPts },
						contentToDelete: tempContentToDelete,
					});
				}
				desc.newActualHeight = currentHeight + footNoteWrapperHeight + newMarginTopInPxl - elBeforeMarginBottom;
			}
		}

	} else if (availableHeightAfterFNW < defaultMarginTopInPxs) {
		// ── SCENARIO 3: not enough space — move FNW up to index - 1 ──

		desc.scenario = 3;
		const nodeBeforeHasSuperscript = hasSuperscriptBefore;
		const availableSpaceForFnwAtLowerIndex = pageHeightInPxs - currentHeight + elBeforeHeight + elBeforeMarginBottom;
		const availableSpaceAfterAddingFNWUp = availableSpaceForFnwAtLowerIndex - footNoteWrapperHeight + elBeforePrevIndexMarginBottom;
		const marginTopNeededInPts = +pxlsToPts(availableSpaceAfterAddingFNWUp).toFixed(2);

		const updatedChildren = nodeBeforeHasSuperscript
			? fnwNode.children.filter(
				(child) => child.superscriptNumber !== nodeBeforeHasSuperscript[0].text
			)
			: fnwNode.children;

		const updatedFootnote = { ...fnwNode, marginTop: marginTopNeededInPts, children: updatedChildren };

		const contentToBeDeleted = nodeBeforeHasSuperscript
			? fnwNode.children.map((child, idx) => {
				const contSuperscriptNumber = +child.superscriptNumber;
				const superscriptToCompare = +nodeBeforeHasSuperscript[0].text;
				return contSuperscriptNumber >= superscriptToCompare ? idx : null;
			}).filter((idx) => idx !== null)
			: [];

		desc.fnwToMove.push({
			at: [index],
			to: [index - 1],
			element: updatedFootnote,
			contentToDelete: tempContentToDelete.length > 0
				? [...contentToBeDeleted, ...tempContentToDelete]
				: contentToBeDeleted,
			contentToAdd: hasRelocatedFootnoteContent[index] && hasRelocatedFootnoteContent[index].contentToAdd
				? hasRelocatedFootnoteContent[index].contentToAdd
				: [],
		});

		if (nodeBeforeHasSuperscript) {
			const nodeBeforeFnwIndex = fnWLists.findIndex(
				(fnw) => fnw.content.includes(nodeBeforeHasSuperscript[0].text)
			);
			const nextFNW = fnWLists.length > nodeBeforeFnwIndex + 1
				? fnWLists[nodeBeforeFnwIndex + 1]
				: null;
			const content = fnwNode.children.filter(
				(child) => child.superscriptNumber === nodeBeforeHasSuperscript[0].text
			);
			const toIndex = nodeBeforeFnwIndex === fnWLists.length - 1
				? null
				: fnWLists[nodeBeforeFnwIndex + 1].at[0];

			if (toIndex) {
				const updatedSuperscript = {
					...superscriptsObject[nodeBeforeHasSuperscript[0].text],
					newContentPath: [toIndex, getNewContentPosition(nextFNW.content, +nodeBeforeHasSuperscript[0].text)],
				};

				const existingRelocation = hasRelocatedFootnoteContent[nextFNW.at[0]];
				desc.relocatedContent = {
					key: nextFNW.at[0],
					value: existingRelocation
						? { contentToAdd: [...existingRelocation.contentToAdd, updatedSuperscript] }
						: { contentToAdd: [updatedSuperscript] },
				};

			} else if (
				(newFootnoteWrappersCreated.length > 0
					&& newFootnoteWrappersCreated[newFootnoteWrappersCreated.length - 1].at !== toIndex)
				|| newFootnoteWrappersCreated.length === 0
			) {
				desc.newFnwToAdd.push({
					at: toIndex,
					content: { type: 'footnote-wrapper', marginTop: 10, children: [...content] },
				});

			} else if (
				newFootnoteWrappersCreated.length > 0
				&& newFootnoteWrappersCreated[newFootnoteWrappersCreated.length - 1].at === toIndex
			) {
				const lastIdx = newFootnoteWrappersCreated.length - 1;
				const last = newFootnoteWrappersCreated[lastIdx];
				desc.newFnwToUpdate = {
					index: lastIdx,
					item: { ...last, children: [...last.children, content] },
				};
			}
		}

		desc.newActualHeight = currentHeight
			+ footNoteWrapperHeight
			+ availableSpaceAfterAddingFNWUp
			- elBeforePrevIndexMarginBottom;

	} else {
		// ── SCENARIO 4: availableHeightAfterFNW ≈ 0, just adjust actualHeight ──

		desc.scenario = 4;
		const marginToAdd = elBeforeMarginBottom > defaultMarginTopInPxs
			? 0
			: defaultMarginTopInPxs - elBeforeMarginBottom;
		desc.newActualHeight = currentHeight + footNoteWrapperHeight + marginToAdd;
	}

	return desc;
}
